import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, Grid, AlertCircle } from 'lucide-react';
import { useConfigurator } from '../ConfiguratorProvider';
import { installationMethods } from '../productData';

export const InstallationMethodSelection = () => {
  const { configuration, setConfiguration } = useConfigurator();

  const handleInstallationMethodSelect = (method: typeof installationMethods[0]) => {
    setConfiguration({ installationMethod: method });
  };

  const getOrientationIcon = (orientation: string) => {
    return orientation === 'portrait' ? RotateCcw : Grid;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">Hoe wil je de panelen plaatsen?</h3>
        <p className="text-muted-foreground">
          De plaatsing beïnvloedt de esthetiek en efficiëntie van je systeem
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {installationMethods.map((method) => {
          const IconComponent = getOrientationIcon(method.orientation);
          
          return (
            <Card 
              key={method.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                configuration.installationMethod?.id === method.id 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : ''
              }`}
              onClick={() => handleInstallationMethodSelect(method)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg flex items-center gap-3">
                    <IconComponent className="h-5 w-5 text-primary" />
                    {method.name}
                  </CardTitle>
                  <Badge variant="outline">
                    {method.rows} rij{method.rows > 1 ? 'en' : ''}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm leading-relaxed mb-4">
                  {method.description}
                </CardDescription>
                
                <div className="bg-muted/50 p-3 rounded-lg mb-4">
                  <div className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Oriëntatie:</span>
                      <span className="font-medium">
                        {method.orientation === 'portrait' ? 'Staand' : 'Liggend'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rijen:</span>
                      <span className="font-medium">{method.rows}</span>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground mb-4">
                  {method.orientation === 'portrait' ? (
                    <span>
                      ✓ Meer panelen per rij mogelijk<br/>
                      ✓ Betere ventilatie tussen rijen
                    </span>
                  ) : (
                    <span>
                      ✓ Lagere montage hoogte<br/>
                      ✓ Stabielere constructie
                    </span>
                  )}
                </div>
                
                {configuration.installationMethod?.id === method.id && (
                  <Button className="w-full" variant="secondary">
                    Geselecteerd ✓
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {configuration.installationMethod && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Grid className="h-4 w-4 text-primary" />
              <span className="font-medium">Jouw selectie:</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {configuration.installationMethod.name} - {configuration.installationMethod.description}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};